import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { combineLatest, merge } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  tap
} from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { LogClass, Logger, LogMethod } from "src/app/logger";
import { SubSink } from "subsink";
import { IPerson } from "../../../interfaces";
import { PeoplePickerComponent } from "../people-picker/people-picker.component";

@LogClass()
@Component({
    selector: "circ-people-picker",
    templateUrl: "./../people-picker/people-picker.component.html",
    styleUrls: ["./../people-picker/people-picker.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CircPeoplePickerComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: CircPeoplePickerComponent,
            multi: true
        }
    ],
    standalone: false
})
export class CircPeoplePickerComponent
  extends PeoplePickerComponent
  implements OnDestroy, OnInit
{
  @Input() public people: IPerson[];
  private subscriptions = new SubSink();
  private logger: Logger;

  constructor(private i18n: I18nService) {
    super();
    this.data = [];
    this.setNoDataText("picker_people_typeToSearch");
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @LogMethod()
  public ngOnInit(): void {
    const searchTextDistinct$ = this.filterChange.pipe(
      this.logger.observableOperator("filterChange"),
      debounceTime(500),
      distinctUntilChanged(),
      this.logger.observableOperator("filterChange - after distinct")
    );

    const noSearchText$ = combineLatest([
      this.focusChange,
      searchTextDistinct$
    ]).pipe(
      map(([, text]) => text),
      filter((text) => !text), // No search Text
      tap(() => {
        this.logger.debug("No search text");
      }),
      map(() => this.people)
    );

    const searchText$ = combineLatest([
      this.focusChange,
      searchTextDistinct$
    ]).pipe(
      filter((text) => !!text), // With search Text
      map(([, text]) => text),
      tap(() => {
        this.logger.debug("Loading");
        this.setNoDataText("picker_people_searching");
        this.loading = true;
      }),
      map((text) => this.people.filter((a) => this.searchIn(a, text))),
      tap((p) => {
        if (p.length === 0) {
          this.setNoDataText("picker_people_noData");
        }
        this.loading = false;
        this.logger.debug("Loading finished");
      })
    );

    this.subscriptions.sink = merge(noSearchText$, searchText$).subscribe(
      (d) => (this.data = d)
    );

    this.filterChange.emit("");
  }

  private setNoDataText(key: string) {
    this.noDataText = "...";
    this.i18n
      .get(key)
      .pipe(first())
      .subscribe((v) => (this.noDataText = v));
  }

  private searchIn(person: IPerson, query: string): boolean {
    for (const prop in person) {
      if (!Object.prototype.hasOwnProperty.call(person, prop)) {
        continue;
      }
      if (typeof person[prop] !== "string" || person[prop] === null) {
        continue;
      }

      if (person[prop].toUpperCase().indexOf(query.toUpperCase()) >= 0) {
        return true;
      }
    }
    return false;
  }
}
